import React from 'react';
import Button from '../common/Button';
import InfoHintButton from '../common/InfoHintButton';
import StatusBadge from '../common/StatusBadge';
import Typography from '../common/Typography';
import { SESSION_PANEL_PADDING } from '../../utils/sessionMobileLayout';
import {
  OWN_PRESENCE_ACTIONS,
  getOwnPresenceAction,
  latePenaltyNotice,
} from '../../utils/sessionPresence';

const STATUS_COPY = {
  signed_up: {
    badge: 'Signed up',
    badgeVariant: 'primary',
    title: 'Mark yourself here',
    body: 'Tell the host you have arrived so you can be picked for games.',
    actionLabel: "I'm here",
    actionVariant: 'primary',
  },
  active: {
    badge: 'Ready',
    badgeVariant: 'success',
    title: "You're in the queue",
    body: '',
    actionLabel: 'Pause',
    actionVariant: 'outline',
  },
  paused: {
    badge: 'Paused',
    badgeVariant: 'warning',
    title: "You're paused",
    body: 'Resume to go back to the end of the queue.',
    actionLabel: 'Resume',
    actionVariant: 'primary',
  },
};

const SessionPresenceCard = ({
  rosterStatus,
  pauseBlockReason = null,
  lateJoinerPenalty = 0,
  onAction,
  loading = false,
  className = '',
}) => {
  const copy = STATUS_COPY[rosterStatus];
  const action = getOwnPresenceAction(rosterStatus);
  if (!copy || !action) return null;

  const pauseBlocked = action === OWN_PRESENCE_ACTIONS.PAUSE && Boolean(pauseBlockReason);
  const penaltyMessage =
    rosterStatus === 'active' ? latePenaltyNotice(lateJoinerPenalty) : null;

  const needsSignIn = rosterStatus === 'signed_up';

  return (
    <section
      className={`rounded-xl bg-surface shadow-brand-sm ${SESSION_PANEL_PADDING} ${
        needsSignIn
          ? 'border-2 border-success ring-1 ring-success/20'
          : 'border border-border-medium'
      } ${className}`}
      aria-label="Your session status"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <Typography variant="h6" as="h2" className="!mb-0">
              {copy.title}
            </Typography>
            <StatusBadge variant={copy.badgeVariant} size="small">
              {copy.badge}
            </StatusBadge>
            {penaltyMessage ? (
              <InfoHintButton
                text={penaltyMessage}
                ariaLabel="Late penalty details"
                className="text-warning-dark"
              />
            ) : null}
          </div>
          {copy.body ? (
            <Typography variant="body-small" className="!mb-0">
              {copy.body}
            </Typography>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {pauseBlocked ? (
            <InfoHintButton
              text={pauseBlockReason}
              ariaLabel="Why you cannot pause"
            />
          ) : null}
          <Button
            type="button"
            variant={copy.actionVariant}
            size="small"
            disabled={loading || pauseBlocked}
            aria-label={copy.actionLabel}
            onClick={() => onAction?.(action)}
          >
            {loading ? 'Updating…' : copy.actionLabel}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SessionPresenceCard;
